import React, { useState, useMemo, useEffect } from 'react';
import apiClient from '../api/apiClient';
import Swal from 'sweetalert2';
import { getUserId } from '../utils/auth';
import './../styles/datatable.css';

const DataTable = ({ data: initialData, endpoint, onDataChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(initialData || []);
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [foreignKeyData, setForeignKeyData] = useState({});
  const [foreignKeyLoading, setForeignKeyLoading] = useState(false);
  const [foreignKeyErrors, setForeignKeyErrors] = useState({});
  const [displayColumns, setDisplayColumns] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const pageSizeOptions = [10, 30, 50, { value: 'all', label: 'All' }];

  const booleanColumns = ['is_active', 'is_test', 'is_processed', 'is_deleted'];
  const readOnlyColumns = ['id', 'file_uuid', 'created_at', 'updated_at', 'token', 'reset_password'];

  const hiddenColumns = useMemo(() => ['id', 'file_uuid', 'token', 'reset_password'], []);
  const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

  const displayBooleanValue = (value) => {
    if (value === true || value === 1 || value === '1') return 'Yes';
    if (value === false || value === 0 || value === '0') return 'No';
    return value;
  };

  const parseBooleanInput = (value) => {
    if (value === 'Yes') return 1;
    if (value === 'No') return 0;
    return value;
  };

  const getForeignKeyName = (column, id) => {
    if (!foreignKeyData[column] || !id) return id;
    const relatedItem = foreignKeyData[column].find(item => item.id === id);
    return relatedItem ? (relatedItem.name || relatedItem.title || relatedItem.label || id) : id;
  };

  const formatColumnLabel = (col) => {
    return col.endsWith('_id')
      ? col.replace('_id', '').replace(/_/g, ' ')
      : col.replace(/_/g, ' ');
  };

  useEffect(() => {
    setData(initialData || []);
    setCurrentPage(1);
  }, [initialData]);

  useEffect(() => {
    if (data.length > 0) {
      const allColumns = Object.keys(data[0]);
      setDisplayColumns(allColumns.filter(col => !hiddenColumns.includes(col)));
    }
  }, [data, hiddenColumns]);

  useEffect(() => {
    if (data.length === 0) return;
    const fkColumns = Object.keys(data[0])
      .filter(col => col.endsWith('_id') && col !== 'id')
      .filter(col => !(col in foreignKeyData));
    if (fkColumns.length === 0) return;

    const fetchForeignKeyData = async () => {
      setForeignKeyLoading(true);
      const newFkData = {};
      const newErrors = {};
      for (const col of fkColumns) {
        let relatedEndpoint = col.replace(/_id$/, '');
        if (relatedEndpoint === 'address') relatedEndpoint = 'addresses';
        else if (relatedEndpoint.endsWith('y')) relatedEndpoint = relatedEndpoint.slice(0, -1) + 'ies';
        else if (relatedEndpoint.endsWith('ch')) relatedEndpoint = relatedEndpoint + 'es';
        else relatedEndpoint = relatedEndpoint + 's';
        try {
          const response = await apiClient.get(`api/${relatedEndpoint}/v1`);
          newFkData[col] = response.data;
        } catch (error) {
          console.error(`Error fetching data for ${relatedEndpoint}:`, error);
          newErrors[col] = `Could not load ${relatedEndpoint}`;
          newFkData[col] = [];
        }
      }
      setForeignKeyData(prev => ({ ...prev, ...newFkData }));
      setForeignKeyErrors(prev => ({ ...prev, ...newErrors }));
      setForeignKeyLoading(false);
    };
    fetchForeignKeyData();
  }, [data, foreignKeyData]);

  const filteredData = data.filter((item) =>
    Object.entries(item).some(([key, value]) => {
      if (key.endsWith('_id') && foreignKeyData[key]) {
        const name = getForeignKeyName(key, value);
        return name && name.toString().toLowerCase().includes(searchTerm.toLowerCase());
      }
      return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
    })
  );

  const indexOfLastItem = itemsPerPage === 'all' ? filteredData.length : currentPage * itemsPerPage;
  const indexOfFirstItem = itemsPerPage === 'all' ? 0 : (currentPage - 1) * itemsPerPage;
  const currentItems = itemsPerPage === 'all' ? filteredData : filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    const editData = { ...item };
    booleanColumns.forEach(col => {
      if (col in editData) editData[col] = displayBooleanValue(editData[col]);
    });
    setFormData(editData);
  };

  const handleCancel = () => { setEditingId(null); setIsAdding(false); };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let dataToSend;
      let response;
      if (editingId) {
        dataToSend = { ...formData };
        booleanColumns.forEach(col => { if (col in dataToSend) dataToSend[col] = parseBooleanInput(dataToSend[col]); });
        response = await apiClient.put(`api/${endpoint}/v1/${editingId}`, dataToSend);
        setData(data.map(item => item.id === editingId ? response.data : item));
        onDataChange?.();
      } else if (isAdding) {
        dataToSend = { ...newItem };
        booleanColumns.forEach(col => { if (col in dataToSend) dataToSend[col] = parseBooleanInput(dataToSend[col]); });
        readOnlyColumns.forEach(col => { delete dataToSend[col]; });
        response = await apiClient.post(`api/${endpoint}/v1`, dataToSend);
        setData([...data, response.data]);
        onDataChange?.();
      }
      if (response?.data?.message) {
        await Swal.fire({ title: 'Success', text: response.data.message, icon: 'success', confirmButtonText: 'OK' });
        try {
          const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
          setData(refreshResponse.data);
          onDataChange?.();
        } catch (refreshError) { console.error('Error refreshing data:', refreshError); }
      }
    } catch (error) {
      console.error('Error saving data:', error);
      const msg = error.response?.data?.message || 'An error occurred while saving the data';
      await Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonText: 'OK' });
    } finally {
      setIsLoading(false);
      setEditingId(null);
      setIsAdding(false);
    }
  };

  const showAudioPlayerModal = async (currentAudioFileId, match) => {
    try {
      const [currentFileUrl, prevFileUrl, nextFileUrl] = await Promise.all([
        apiClient.get(`/api/audio_files/supply-audio-file/v1/${currentAudioFileId}`, { responseType: 'blob' }).then(res => URL.createObjectURL(res.data)),
        currentAudioFileId > 1 ? apiClient.get(`/api/audio_files/supply-audio-file/v1/${currentAudioFileId - 1}`, { responseType: 'blob' }).then(res => URL.createObjectURL(res.data)).catch(() => null) : Promise.resolve(null),
        apiClient.get(`/api/audio_files/supply-audio-file/v1/${currentAudioFileId + 1}`, { responseType: 'blob' }).then(res => URL.createObjectURL(res.data)).catch(() => null)
      ]);
      let currentIndex = 0;
      const audioFiles = [
        { id: currentAudioFileId - 1, url: prevFileUrl },
        { id: currentAudioFileId, url: currentFileUrl },
        { id: currentAudioFileId + 1, url: nextFileUrl }
      ].filter(file => file.url !== null);
      currentIndex = audioFiles.findIndex(file => file.id === currentAudioFileId);
      let clipInProgress = null;
      const completedClips = [];
      let currentTimeInterval = null;

      const createPlayerHTML = (file) => `
        <div class="audio-player-current">
          <audio id="audio-player" controls src="${file.url}" style="width: 100%"></audio>
          <div class="time-selection">
            <div><label>Current Time (mm:ss):</label><span id="current-time-display">00:00</span></div>
            <div class="clip-controls">
              <button id="set-start-btn" class="time-btn">${clipInProgress ? 'Update Start' : 'Set Start'}</button>
              <button id="set-end-btn" class="time-btn" ${!clipInProgress ? 'disabled' : ''}>Set End</button>
              <button id="cancel-clip-btn" class="time-btn" ${!clipInProgress ? 'disabled' : ''}>Cancel</button>
            </div>
          </div>
        </div>`;

      const modal = await Swal.fire({
        title: 'Create Audio Clip',
        html: `<div class="audio-clip-interface">${createPlayerHTML(audioFiles[currentIndex])}<div class="navigation-controls"><button id="prev-btn" class="nav-btn" ${currentIndex === 0 ? 'disabled' : ''}>← Previous</button><button id="next-btn" class="nav-btn" ${currentIndex === audioFiles.length - 1 ? 'disabled' : ''}>Next →</button></div><div class="clip-preview"><h4>Current Clip:</h4><div id="current-clip-display">${clipInProgress ? `Start: File ${clipInProgress.startFileId} @ ${clipInProgress.startTime}` : 'No clip in progress'}</div><h4>Completed Clips:</h4><div id="completed-clips-list"></div></div></div>`,
        focusConfirm: false, showCancelButton: true, confirmButtonText: 'Save All Clips', cancelButtonText: 'Cancel', width: '600px',
        didOpen: () => {
          const audioPlayer = document.getElementById('audio-player');
          let cti;
          if (!audioPlayer) return;
          const updateCurrentTime = () => {
            const ap = document.getElementById('audio-player');
            const td = document.getElementById('current-time-display');
            if (!ap || !td) return;
            const ct = ap.currentTime;
            td.textContent = `${Math.floor(ct / 60).toString().padStart(2, '0')}:${Math.floor(ct % 60).toString().padStart(2, '0')}`;
          };
          cti = setInterval(updateCurrentTime, 200);
          audioPlayer.addEventListener('play', updateCurrentTime);
          audioPlayer.addEventListener('seeked', updateCurrentTime);
          const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
          const updateClipDisplays = () => {
            document.getElementById('current-clip-display').innerHTML = clipInProgress ? `Start: File ${clipInProgress.startFileId} @ ${clipInProgress.startTime}` : 'No clip in progress';
            document.getElementById('completed-clips-list').innerHTML = completedClips.map((clip, i) => `<div class="completed-clip">Clip ${i + 1}: File ${clip.startFileId} @ ${clip.startTime} → ${clip.endFileId !== clip.startFileId ? `File ${clip.endFileId} ` : ''}@ ${clip.endTime}<button class="remove-clip-btn" data-index="${i}">×</button></div>`).join('');
            document.querySelectorAll('.remove-clip-btn').forEach(btn => btn.addEventListener('click', (e) => { completedClips.splice(parseInt(e.target.dataset.index), 1); updateClipDisplays(); }));
          };
          const attachClipHandlers = (ap) => {
            document.getElementById('set-start-btn')?.addEventListener('click', () => { clipInProgress = { startFileId: audioFiles[currentIndex].id, startTime: formatTime(ap.currentTime), endFileId: null, endTime: null }; document.getElementById('set-end-btn').disabled = false; document.getElementById('cancel-clip-btn').disabled = false; updateClipDisplays(); });
            document.getElementById('set-end-btn')?.addEventListener('click', () => { if (!clipInProgress) return; clipInProgress.endFileId = audioFiles[currentIndex].id; clipInProgress.endTime = formatTime(ap.currentTime); completedClips.push(clipInProgress); clipInProgress = null; document.getElementById('set-end-btn').disabled = true; document.getElementById('cancel-clip-btn').disabled = true; updateClipDisplays(); });
            document.getElementById('cancel-clip-btn')?.addEventListener('click', () => { clipInProgress = null; document.getElementById('set-end-btn').disabled = true; document.getElementById('cancel-clip-btn').disabled = true; updateClipDisplays(); });
          };
          attachClipHandlers(audioPlayer);
          const updatePlayer = () => {
            clearInterval(cti);
            document.querySelector('.audio-player-current').innerHTML = createPlayerHTML(audioFiles[currentIndex]);
            const nap = document.getElementById('audio-player');
            nap.addEventListener('play', updateCurrentTime);
            nap.addEventListener('seeked', updateCurrentTime);
            cti = setInterval(updateCurrentTime, 200);
            attachClipHandlers(nap);
            document.getElementById('prev-btn').disabled = currentIndex === 0;
            document.getElementById('next-btn').disabled = currentIndex === audioFiles.length - 1;
          };
          document.getElementById('prev-btn')?.addEventListener('click', () => { if (currentIndex > 0) { currentIndex--; updatePlayer(); } });
          document.getElementById('next-btn')?.addEventListener('click', () => { if (currentIndex < audioFiles.length - 1) { currentIndex++; updatePlayer(); } });
          updateClipDisplays();
        },
        preConfirm: () => {
          if (completedClips.length === 0) { Swal.showValidationMessage('Please create at least one clip'); return false; }
          return { keyword_id: match.keyword_id, match_id: match.id, user_id: getUserId(), clips: completedClips.map(clip => ({ start_audio_file_id: clip.startFileId, end_audio_file_id: clip.endFileId, start_time: clip.startTime, end_time: clip.endTime })) };
        }
      });
      if (modal.isConfirmed) { await apiClient.post('/api/audio_clips/set-clips/v1', modal.value); Swal.fire('Success!', 'Audio clips saved successfully', 'success'); }
      audioFiles.forEach(file => { if (file.url) URL.revokeObjectURL(file.url); });
      if (currentTimeInterval) clearInterval(currentTimeInterval);
    } catch (error) {
      console.error('Error in audio player modal:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to create audio clip', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Are you sure?', text: 'This action cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes, delete it', cancelButtonText: 'Cancel' });
    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const response = await apiClient.delete(`api/${endpoint}/v1/${id}`);
        setData(data.filter(item => item.id !== id));
        onDataChange?.();
        if (response?.data?.message) {
          await Swal.fire({ title: 'Deleted', text: response.data.message, icon: 'success', confirmButtonText: 'OK' });
          try { const rr = await apiClient.get(`api/${endpoint}/v1`); setData(rr.data); onDataChange?.(); } catch (e) { console.error('Error refreshing:', e); }
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        if (error.response?.data?.message) await Swal.fire({ title: 'Error', text: error.response.data.message, icon: 'error', confirmButtonText: 'OK' });
      } finally { setIsLoading(false); }
    }
  };

  const handleCaptureAudio = async (match) => {
    const userId = getUserId();
    try {
      await apiClient.put(`/api/matches/set-match-captured/v1/${match.id}`, { match_state: '1', user_id: userId });
      showAudioPlayerModal(match.audio_file_id, match);
      const response = await apiClient.get(`api/${endpoint}/v1`);
      setData(response.data);
      onDataChange?.();
    } catch (error) {
      console.error('Error capturing audio:', error);
      Swal.fire({ title: 'Error', text: error.response?.data?.message || 'Failed to capture audio', icon: 'error', confirmButtonText: 'OK' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (editingId) setFormData({ ...formData, [name]: value });
    else if (isAdding) setNewItem({ ...newItem, [name]: value });
  };

  const handleAddNew = () => {
    setIsAdding(true);
    if (data.length > 0) {
      const emptyItem = {};
      Object.keys(data[0]).forEach(key => {
        emptyItem[key] = booleanColumns.includes(key) ? 'No' : '';
        if (readOnlyColumns.includes(key)) delete emptyItem[key];
      });
      setNewItem(emptyItem);
    }
  };

  // Rendering helpers
  const renderFieldInput = (col, value, onChange, isNew = false) => {
    if (booleanColumns.includes(col)) {
      return (<select name={col} value={value || 'No'} onChange={onChange}><option value="Yes">Yes</option><option value="No">No</option></select>);
    }
    if (col === 'day_of_week') {
      return (<select name={col} value={value || ''} onChange={onChange}><option value="">Select Day</option>{weekdays.map(day => <option key={day} value={day}>{day}</option>)}</select>);
    }
    if (col.endsWith('_id') && foreignKeyData[col]) {
      if (foreignKeyErrors[col]) return <div className="error-message">{foreignKeyErrors[col]}</div>;
      return (<select name={col} value={value || ''} onChange={onChange} disabled={foreignKeyLoading}><option value="">Select {formatColumnLabel(col)}</option>{foreignKeyData[col].map(item => <option key={item.id} value={item.id}>{item.name || item.title || item.label || item.id}</option>)}</select>);
    }
    return (<input type="text" name={col} value={value || ''} onChange={onChange} disabled={col === 'id' || readOnlyColumns.includes(col)} placeholder={isNew ? `Enter ${formatColumnLabel(col)}` : ''} />);
  };

  const allColumns = data.length > 0
    ? Object.keys(data[0]).filter(col => !(isAdding && readOnlyColumns.includes(col)) && !hiddenColumns.includes(col))
    : [];

  const renderAddForm = () => (
    <div className="add-form">
      <h3>Add New Item</h3>
      <div className="form-fields">
        {allColumns.map(col => (
          <div key={`new-${col}`} className="form-field">
            <label>{formatColumnLabel(col)}</label>
            {renderFieldInput(col, newItem[col], handleChange, true)}
          </div>
        ))}
      </div>
      <div className="form-actions">
        <button onClick={handleSave} disabled={isLoading}>{isLoading ? 'Saving...' : '✓ Save'}</button>
        <button onClick={handleCancel} disabled={isLoading}>Cancel</button>
      </div>
    </div>
  );

  const renderPagination = (position) => (
    itemsPerPage !== 'all' && (
      <div className={`pagination-controls ${position}`}>
        {position !== 'bottom' && (
          <div className="items-per-page">
            <label>Show:</label>
            <select value={itemsPerPage} onChange={handleItemsPerPageChange} disabled={isLoading}>
              {pageSizeOptions.map(option => <option key={option.value || option} value={option.value || option}>{option.label || option}</option>)}
            </select>
          </div>
        )}
        <div className="pagination-navigation">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1 || isLoading}>« First</button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading}>‹ Prev</button>
          <span className="page-info">
            {position === 'bottom' ? `Page ${currentPage} of ${totalPages} · ${currentItems.length} of ${filteredData.length} items` : `Page ${currentPage} of ${totalPages}`}
          </span>
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading}>Next ›</button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || isLoading}>Last »</button>
        </div>
      </div>
    )
  );

  if (!data || data.length === 0) {
    return (
      <div className="data-table">
        <button className="btn-add" onClick={handleAddNew}>＋ Add New</button>
        {isAdding && renderAddForm()}
        <div className="empty-state"><p>No data available for this section.</p></div>
      </div>
    );
  }

  return (
    <div className="data-table">
      <div className="search-bar">
        <input type="text" placeholder="Search records..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <button className="btn-add" onClick={handleAddNew}>＋ Add New</button>
      </div>

      <div className="record-count">
        Showing <strong>{currentItems.length}</strong> of <strong>{filteredData.length}</strong> records
        {searchTerm && <> (filtered from {data.length} total)</>}
      </div>

      {renderPagination('top')}
      {isAdding && renderAddForm()}

      <div className="table-scroll-container">
        <table>
          <thead>
            <tr>
              {displayColumns.map(col => <th key={col}>{formatColumnLabel(col)}</th>)}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item) => (
              <tr key={item.id}>
                {displayColumns.map(col => (
                  <td key={`${item.id}-${col}`} data-label={formatColumnLabel(col)}>
                    {editingId === item.id ? (
                      readOnlyColumns.includes(col)
                        ? item[col]
                        : renderFieldInput(col, formData[col], handleChange)
                    ) : (
                      booleanColumns.includes(col)
                        ? displayBooleanValue(item[col])
                        : col.endsWith('_id')
                          ? getForeignKeyName(col, item[col])
                          : item[col]
                    )}
                  </td>
                ))}
                <td data-label="Actions">
                  {editingId === item.id ? (
                    <>
                      <button className="btn-save" onClick={handleSave} disabled={isLoading}>{isLoading ? '...' : '✓ Save'}</button>
                      <button className="btn-cancel" onClick={handleCancel} disabled={isLoading}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button className="btn-edit" onClick={() => handleEdit(item)} disabled={isLoading || isAdding}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(item.id)} disabled={isLoading || isAdding}>Delete</button>
                      {endpoint === 'matches' && item.match_state === "0" && (
                        <button className="btn-capture" onClick={() => handleCaptureAudio(item)} disabled={isLoading || isAdding}>🎙 Capture</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {renderPagination('bottom')}
    </div>
  );
};

export default DataTable;
